// ./src/components/features/Policies/PoliciesList/PoliciesList.tsx
//
// Policies list — table on desktop (>= md), cards on tablet/mobile. Tap-safe
// (explicit Edit/Delete only), rights-gated per row, and selection-ready.
// Mirrors the Highlights list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { Policy } from "@/utils/api";
import { policyAcceptanceSummary } from "@/utils/complianceSetup";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalPoliciesContext } from "@/contexts/PoliciesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Props
// ============================================

export interface PoliciesListProps {
  data?: Policy[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditPolicy?: (policy: Policy) => void;
  onDeletePolicy?: (policy: Policy) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddPolicy?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (policy: Policy): boolean => policy.status === STATUSES.DISABLED;


const StatusBadge: React.FC<{ policy: Policy }> = ({ policy }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(policy)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

// Kind + signing + age requirement badges — the "what this policy asks of guests".
const RequirementBadges: React.FC<{ policy: Policy }> = ({ policy }) => {
  const { t } = useTranslation(["policies"]);

  const ageLabel = useMemo(() => {
    const { minAge, maxAge } = policy;

    if (minAge != null && maxAge != null) {
      return t("policies:age.range", { defaultValue: "Ages {{min}}–{{max}}", min: minAge, max: maxAge });
    }

    if (minAge != null) {
      return t("policies:age.min", { defaultValue: "Ages {{min}}+", min: minAge });
    }

    if (maxAge != null) {
      return t("policies:age.max", { defaultValue: "Up to {{max}}", max: maxAge });
    }

    return null;
  }, [policy, t]);

  const how = policyAcceptanceSummary(policy);

  return (
    <Group gap={6} wrap="wrap">
      <Badge size="xs" variant="light" color={how.hasText ? "grape" : "blue"}>
        {how.hasText
          ? t("policies:kind.document", { defaultValue: "Document" })
          : t("policies:kind.checkbox", { defaultValue: "Checkbox" })}
      </Badge>

      {/* #164 — how a document is accepted: ticked on the form, or by a link to each adult / guardian. */}
      {how.hasText && (
        <Badge size="xs" variant="outline" color={how.byLink ? "grape" : "blue"}>
          {how.byLink
            ? t("policies:kind.byLink", { defaultValue: "Link to accept" })
            : t("policies:kind.tickBox", { defaultValue: "Tick box" })}
        </Badge>
      )}

      {how.signature && (
        <Badge size="xs" variant="outline" color="teal" leftSection={<AppIcon iconName="Signature" size={10} />}>
          {t("policies:req.signature", { defaultValue: "Signature" })}
        </Badge>
      )}

      {how.initials && (
        <Badge size="xs" variant="outline" color="cyan" leftSection={<AppIcon iconName="Edit" size={10} />}>
          {t("policies:req.initials", { defaultValue: "Initials" })}
        </Badge>
      )}

      {ageLabel && (
        <Badge size="xs" variant="light" color="orange">
          {ageLabel}
        </Badge>
      )}
    </Group>
  );
};

const PolicyIcon: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <ThemeIcon variant="light" color="grape" radius="xl" size={size}>
    <AppIcon iconName="Gavel" size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  policy: Policy;
  onEdit?: (p: Policy) => void;
  onDelete?: (p: Policy) => void;
  isDeleting: boolean;
}> = ({ policy, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(policy, "edit");
  const showDelete = !!onDelete && recordHasRight(policy, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(policy)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(policy)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  policy: Policy;
  name: string;
  disabled: boolean;
  onEdit?: (p: Policy) => void;
  onDelete?: (p: Policy) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const PolicyTableRow: React.FC<RowProps> = ({
  policy,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => policy.id && onToggleSelect?.(policy.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <PolicyIcon />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <StatusBadge policy={policy} />
            </Group>
            {policy.label && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {policy.label}
              </Text>
            )}
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 320 }}>
        <RequirementBadges policy={policy} />
      </Table.Td>
      <Table.Td style={{ width: 170 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(policy.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions policy={policy} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const PolicyTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 320 }}>
      <Skeleton height={18} width={140} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const PolicyCardComponent: React.FC<CardProps> = ({
  policy,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => policy.id && onToggleSelect?.(policy.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(policy, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(policy),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(policy, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(policy),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, policy, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <PolicyIcon size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge policy={policy} />
          </Group>
          {policy.label && (
            <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
              {policy.label}
            </Text>
          )}
          <Box mt={6}>
            <RequirementBadges policy={policy} />
          </Box>
        </Box>
        {showActions && (
          <RowActions policy={policy} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const PolicyCard = React.memo(PolicyCardComponent);

const PolicyCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={["md", "xs", "xs"]} gap={4} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const PoliciesListComponent: React.FC<PoliciesListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditPolicy,
  onDeletePolicy,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddPolicy,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["policies", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditPolicy, onDeletePolicy);

  const policiesContext = useOptionalPoliciesContext();

  const policies = dataProp ?? policiesContext?.policies ?? [];
  const loading = loadingProp ?? policiesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? policiesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? policiesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? policiesContext?.loadMore;
  const isRemoving = isRemovingProp ?? policiesContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: policies.length });

  const handleEdit = useCallback(
    (policy: Policy) => {
      closeAllCards();
      onEditPolicy?.(policy);
    },
    [onEditPolicy, closeAllCards]
  );

  const handleDelete = useCallback(
    (policy: Policy) => {
      closeAllCards();
      onDeletePolicy?.(policy);
    },
    [onDeletePolicy, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = policiesContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && policies.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("policies:emptyState.noMatch", { defaultValue: "No policies match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("policies:emptyState.title", { defaultValue: "No policies yet" })}
          description={t("policies:emptyState.description", {
            defaultValue: "Create a reusable waiver, consent or house rule to attach to your events.",
          })}
          iconName="Gavel"
          actionLabel={t("policies:emptyState.actionLabel", { defaultValue: "Add policy" })}
          onAction={onAddPolicy}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (policy: Policy): boolean =>
    isSelectable &&
    ((!!onEditPolicy && recordHasRight(policy, "edit")) ||
      (!!onDeletePolicy && recordHasRight(policy, "remove")));

  const rowProps = (policy: Policy): RowProps => ({
    policy,
    name: policy.name || "—",
    disabled: isDisabled(policy),
    onEdit: onEditPolicy ? handleEdit : undefined,
    onDelete: onDeletePolicy ? handleDelete : undefined,
    highlighted: policy.id === highlightedId,
    isDeleting: isRemoving(policy.id),
    language,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(policy),
    selected: selection?.isSelected(policy.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = policies
    .filter((p) => rowIsSelectable(p))
    .map((p) => p.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("policies:list.columns.name", { defaultValue: "Policy" })}</Table.Th>
                <Table.Th style={{ width: 320 }}>
                  {t("policies:list.columns.requirements", { defaultValue: "Requirements" })}
                </Table.Th>
                <Table.Th style={{ width: 170 }}>
                  {t("policies:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <PolicyTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : policies.map((policy) => <PolicyTableRow key={policy.id} {...rowProps(policy)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <PolicyCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {policies.map((policy) => (
            <motion.div
              key={policy.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <PolicyCard
                {...rowProps(policy)}
                forceClose={shouldForceClose(policy.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(policy.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(policy.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const PoliciesList = React.memo(PoliciesListComponent);
